import { useState } from "react";
import { Check, Copy, Code } from "lucide-react";

/**
 * CodeBlockRenderer — Read-only syntax-highlighted code display
 * 
 * Expected item shape:
 * {
 *   type: "code_block",
 *   language: "python" | "javascript" | "sql" | "json" | etc,
 *   code: "def hello():\n    print('world')",
 *   title: "Example: Sorting Algorithm" (optional)
 * }
 */
export function CodeBlockRenderer({ item, index }) {
  const [copied, setCopied] = useState(false);
  const language = item.language || "text";
  const code = item.code || "";
  const title = item.title || "";
  const lines = code.split("\n");

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = code;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div key={index} className="my-4" data-testid={`code-block-${index}`}>
      {/* Header bar */}
      <div className="flex items-center justify-between bg-[#1e1e2e] text-gray-300 px-4 py-2 rounded-t-lg border border-b-0 border-[#2a2a3e]">
        <div className="flex items-center gap-2">
          <Code className="w-4 h-4 text-[#00AEEF]" />
          {title && <span className="text-sm font-medium text-gray-200">{title}</span>}
          <span className="text-xs bg-[#2a2a3e] px-2 py-0.5 rounded text-gray-400 uppercase tracking-wide">
            {language}
          </span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-xs text-gray-400 hover:text-white transition-colors px-2 py-1 rounded hover:bg-[#2a2a3e]"
          data-testid={`code-block-copy-${index}`}
        >
          {copied ? (
            <><Check className="w-3.5 h-3.5 text-green-400" /> Copied</>
          ) : (
            <><Copy className="w-3.5 h-3.5" /> Copy</>
          )}
        </button>
      </div>
      {/* Code body */}
      <div className="bg-[#1e1e2e] rounded-b-lg border border-t-0 border-[#2a2a3e] overflow-x-auto">
        <pre className="p-0 m-0">
          <code className="block text-sm leading-6">
            {lines.map((line, lineIdx) => (
              <div key={lineIdx} className="flex hover:bg-[#2a2a3e]/50">
                <span className="select-none text-right text-gray-600 w-12 pr-4 pl-3 flex-shrink-0 text-xs leading-6">
                  {lineIdx + 1}
                </span>
                <span className="text-gray-200 pr-4 whitespace-pre font-mono text-[13px] leading-6">
                  {line || " "}
                </span>
              </div>
            ))}
          </code>
        </pre>
      </div>
    </div>
  );
}
